/**
 * Access — managers-only access management for one app:
 *   - Toggle workspace visibility ("Anyone at Mytra can view")
 *   - Add / remove Managers — platform access (deploy, settings, secrets, roles)
 *   - Add / remove App-role grants — Admin / Member, read by the app's own code
 *
 * Managers and app roles are orthogonal axes: a person can be a manager, hold
 * an app role, both, or neither.
 *
 * Backed by:
 *   - PATCH  /api/apps/:slug/visibility
 *   - POST   /api/apps/:slug/roles                       { email, role: manager|admin|member }
 *   - DELETE /api/apps/:slug/roles/:email?role=manager|admin|member
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import type { AppManager, AppRoleGrant } from '../api/types.js';
import { AppTabs } from '../components/AppTabs.js';
import { Avatar } from '../components/Avatar.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { IconGlobe, IconLock, IconTrash } from '../components/Icons.js';
import { UserCombobox } from '../components/UserCombobox.js';
import {
  Banner,
  Button,
  ButtonLink,
  Card,
  CardHeader,
  Field,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

const APP_ROLES = ['admin', 'member'] as const;
type AppRoleValue = (typeof APP_ROLES)[number];

interface ShareRolesPayload {
  managers: AppManager[];
  roles: AppRoleGrant[];
}

export function Share() {
  const { slug } = useParams();
  const { app, payload, loading, error, reload, setApp, setPayload } =
    useAppPageWith<ShareRolesPayload>(slug, (s) =>
      api.get<ShareRolesPayload>(`/api/apps/${encodeURIComponent(s)}/roles`),
    );
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [newManagerEmail, setNewManagerEmail] = useState('');
  const [newRoleEmail, setNewRoleEmail] = useState('');
  const [newRole, setNewRole] = useState<AppRoleValue>('member');

  if (loading) return <PageSkeleton rows={4} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;
  const { managers, roles } = payload;
  const isWorkspace = app.visibility === 'workspace';

  if (!app.is_manager) {
    return (
      <ManagerOnlyPage
        app={app}
        title="Access"
        description="Decide who can see, manage, and use this app."
      />
    );
  }

  async function setVisibility(next: 'private' | 'workspace') {
    if (!slug) return;
    await runAction({
      do: () => api.patch(`/api/apps/${encodeURIComponent(slug)}/visibility`, { visibility: next }),
      onSuccess: () =>
        next === 'workspace'
          ? 'Anyone at Mytra can now view this app.'
          : 'Visibility set back to private.',
      errorPrefix: "Couldn't change visibility",
      setBusy,
      setMessage: setActionMessage,
    });
    // Local update — avoids re-fetching the entire managers + roles payload
    // for a single-field change.
    setApp((prev) => ({ ...prev, visibility: next }));
  }

  async function addManager(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !newManagerEmail) return;
    const email = newManagerEmail;
    await runAction({
      do: () => api.post(`/api/apps/${encodeURIComponent(slug)}/roles`, { email, role: 'manager' }),
      onSuccess: () => {
        setNewManagerEmail('');
        return `${email} is now a manager.`;
      },
      errorPrefix: "Couldn't add manager",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function addRole(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !newRoleEmail) return;
    const email = newRoleEmail;
    const role = newRole;
    await runAction({
      do: () => api.post(`/api/apps/${encodeURIComponent(slug)}/roles`, { email, role }),
      onSuccess: () => {
        setNewRoleEmail('');
        setNewRole('member');
        return `${email} is now ${role === 'admin' ? 'an admin' : 'a member'}.`;
      },
      errorPrefix: "Couldn't add",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function changeRole(email: string, role: AppRoleValue) {
    if (!slug) return;
    await runAction({
      // POST upserts the app-role grant, so this changes the role in place.
      do: () => api.post(`/api/apps/${encodeURIComponent(slug)}/roles`, { email, role }),
      onSuccess: () => `${email} is now ${role === 'admin' ? 'an admin' : 'a member'}.`,
      errorPrefix: "Couldn't update",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function revoke(email: string, role: 'manager' | AppRoleValue) {
    if (!slug) return;
    const label = role === 'manager' ? 'manager' : role;
    if (!confirm(`Remove ${email}'s ${label} access?`)) return;
    await runAction({
      do: () =>
        api.delete(
          `/api/apps/${encodeURIComponent(slug)}/roles/${encodeURIComponent(email)}?role=${role}`,
        ),
      onSuccess: () => `Removed ${email}'s ${label} access.`,
      errorPrefix: "Couldn't remove",
      setBusy,
      setMessage: setActionMessage,
    });
    // Optimistic local removal — server confirmed.
    setPayload((prev) => ({
      managers: role === 'manager' ? prev.managers.filter((m) => m.email !== email) : prev.managers,
      roles:
        role === 'manager'
          ? prev.roles
          : prev.roles.filter((r) => !(r.email === email && r.role === role)),
    }));
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Access"
        description="Decide who can see, manage, and use this app. Managers only."
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-6">
          {actionMessage && (
            <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
              {actionMessage.text}
            </Banner>
          )}

          {/* Visibility — big binary state, color + icon answer at a glance. */}
          <Card
            className={
              isWorkspace ? '!border-info/30 !bg-info-soft/40' : '!border-line !bg-surface'
            }
          >
            <div className="p-5 flex items-start gap-4">
              <div
                className={
                  'flex-none w-11 h-11 rounded-xl flex items-center justify-center ' +
                  (isWorkspace ? 'bg-info/15 text-info' : 'bg-surface-2 text-muted')
                }
                aria-hidden
              >
                {isWorkspace ? <IconGlobe size={20} /> : <IconLock size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-lg font-semibold tracking-tight">
                  {isWorkspace
                    ? 'Anyone at Mytra can view this app'
                    : 'Private — only people listed below can reach it'}
                </h2>
                <p className="text-muted text-sm mt-1">
                  {isWorkspace
                    ? 'Every Mytra employee can open the app and find it on the Apps page. Managing still requires being a manager.'
                    : "Even with a link, nobody at Mytra can open it unless they're listed below or you turn on workspace visibility."}
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isWorkspace}
                    disabled={busy}
                    onClick={() => void setVisibility(isWorkspace ? 'private' : 'workspace')}
                    className={
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
                      (isWorkspace ? 'bg-brand' : 'bg-surface-3 ring-1 ring-inset ring-line')
                    }
                  >
                    <span
                      className={
                        'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
                        (isWorkspace ? 'translate-x-5' : 'translate-x-0.5')
                      }
                    />
                  </button>
                  <span className="text-sm font-medium text-ink">Visible to everyone at Mytra</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Managers — platform access. */}
          <Card>
            <CardHeader
              title="Managers"
              description="Can deploy, change settings and secrets, and manage access."
            />
            <form
              onSubmit={(e) => void addManager(e)}
              className="px-5 py-4 flex flex-wrap items-end gap-3 border-b border-line bg-surface-2/40"
            >
              <div className="flex-1 min-w-[220px]">
                <Field label="Email">
                  <UserCombobox
                    value={newManagerEmail}
                    onChange={setNewManagerEmail}
                    placeholder="someone@mytra.ai"
                    required
                  />
                </Field>
              </div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={busy}
                disabled={busy || !newManagerEmail}
              >
                Add manager
              </Button>
            </form>
            {managers.length === 0 ? (
              <p className="px-5 py-5 text-muted text-sm">No managers yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {managers.map((m) => (
                  <li
                    key={m.user_id}
                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                  >
                    <span className="min-w-0 truncate inline-flex items-center gap-2">
                      <Avatar user={m} size={24} />
                      <span className="min-w-0 truncate">
                        <span className="font-medium text-ink">{m.name ?? m.email}</span>
                        {m.name && <span className="text-muted ml-2 text-xs">{m.email}</span>}
                        <span className="text-xs text-faint ml-2">{relTime(m.created_at)}</span>
                      </span>
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void revoke(m.email, 'manager')}
                      disabled={busy}
                      className="!text-danger hover:!bg-danger-soft"
                    >
                      <IconTrash size={13} />
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* App roles — runtime authorization. */}
          <Card>
            <CardHeader
              title="App roles"
              description="Admin / Member — read by the app's own code to gate features. Independent of being a manager."
            />
            <form
              onSubmit={(e) => void addRole(e)}
              className="px-5 py-4 flex flex-wrap items-end gap-3 border-b border-line bg-surface-2/40"
            >
              <div className="flex-1 min-w-[220px]">
                <Field label="Email">
                  <UserCombobox
                    value={newRoleEmail}
                    onChange={setNewRoleEmail}
                    placeholder="someone@mytra.ai"
                    required
                  />
                </Field>
              </div>
              <Field label="Role">
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as AppRoleValue)}
                  className={inputClass}
                >
                  {APP_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </Field>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={busy}
                disabled={busy || !newRoleEmail}
              >
                Add
              </Button>
            </form>
            {roles.length === 0 ? (
              <p className="px-5 py-5 text-muted text-sm">No app-role grants yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {roles.map((r) => (
                  <li
                    key={r.user_id}
                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                  >
                    <span className="min-w-0 truncate inline-flex items-center gap-2">
                      <Avatar user={r} size={24} />
                      <span className="min-w-0 truncate">
                        <span className="font-medium text-ink">{r.name ?? r.email}</span>
                        {r.name && <span className="text-muted ml-2 text-xs">{r.email}</span>}
                        <span className="text-xs text-faint ml-2">{relTime(r.created_at)}</span>
                      </span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <select
                        value={r.role}
                        disabled={busy}
                        onChange={(e) => void changeRole(r.email, e.target.value as AppRoleValue)}
                        aria-label={`Role for ${r.email}`}
                        className={`${inputClass} !w-auto !py-1.5`}
                      >
                        {APP_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void revoke(r.email, r.role)}
                        disabled={busy}
                        className="!text-danger hover:!bg-danger-soft"
                      >
                        <IconTrash size={13} />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Tokens are the programmatic side of access — people above,
              bearer + deploy tokens (CI) on their own page. CardHeader's
              layout without its divider, since this card has no body. */}
          <Card>
            <div className="flex items-start justify-between gap-4 px-5 py-4">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold tracking-tight">Tokens</h2>
                <p className="text-muted text-xs mt-0.5">
                  Bearer tokens for MCP clients and scripts, and deploy tokens for CI.
                </p>
              </div>
              <ButtonLink
                to={`/apps/${app.slug}/tokens`}
                variant="ghost"
                size="sm"
                className="shrink-0"
              >
                Manage tokens
              </ButtonLink>
            </div>
          </Card>
        </main>
      </div>
    </div>
  );
}
